import { onBeforeUnmount, onMounted } from 'vue';

// Bridge the VisualViewport API to CSS. On iOS the on-screen keyboard shrinks
// the VISUAL viewport but not the LAYOUT viewport - so `vh` / `dvh` and
// `position: fixed` elements ignore the keyboard and content ends up behind it.
// `interactive-widget=resizes-content` fixes this on Chrome/Android, but iOS
// Safari doesn't support it; VisualViewport (which iOS does support) is the only
// reliable signal. We publish it as CSS custom properties on <html> so any
// overlay can size itself to the area the keyboard leaves:
//   --vvh : the visual viewport height in px (shrinks when the keyboard opens)
//   --kb  : the keyboard inset at the bottom in px (0 when closed)
//
// Call once, app-wide (AppShell). No-op where VisualViewport is unavailable; the
// consumers fall back to dvh via `var(--vvh, 100dvh)`.
export function useVisualViewport(): void {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;

  function update(): void {
    if (!vv) return;
    const root = document.documentElement;
    root.style.setProperty('--vvh', `${Math.round(vv.height)}px`);
    // How much of the layout viewport's bottom the keyboard covers. offsetTop
    // accounts for any visual-viewport scroll so this stays correct mid-scroll.
    const kb = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
    root.style.setProperty('--kb', `${kb}px`);
  }

  onMounted(() => {
    if (!vv) return;
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
  });
  onBeforeUnmount(() => {
    if (!vv) return;
    vv.removeEventListener('resize', update);
    vv.removeEventListener('scroll', update);
  });
}
